import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { FileRemoveEvent, FileSelectEvent, FileUpload, FileUploadModule } from "primeng/fileupload";
import { FileSizePipe } from "../../../pipes/file-size.pipe";
import { reportToSentry } from "../../../sentry/reportFunctions";
import { PrimeNgMessageSeverity } from "../../../types/primeng";
import { LayoutService } from "../../apollo-template/service/layout.service";

@Component({
  selector: "app-file-upload",
  templateUrl: "./file-upload.component.html",
  styleUrl: "./file-upload.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FileUploadModule, TranslocoDirective, FileSizePipe],
})
export class FileUploadComponent implements AfterViewInit {
  @ViewChild("fileUpload") fileUploadComponent!: FileUpload;

  @Input() name = "files";
  @Input() isInvalid = false;
  @Input() isDirty = false;
  @Input() fileTypes?: string; // e.g., ".pdf,.doc,.docx,.jpg,.png"
  @Input() maxFileSize = 50 * 1024 * 1024; // Default 50MB

  private _tempFiles?: File[];
  @Input() set files(value: File[]) {
    if (!this.fileUploadComponent) {
      // If the component isn't available yet, temporarily stash the variable in a local,
      // and we'll fill it in in ngAfterViewInit()
      this._tempFiles = value;
      return;
    }

    if (value !== this.fileUploadComponent.files) {
      this.fileUploadComponent.files = value;
    }
    // If cleared, empty out the error list, as they're stale now
    if (value.length === 0) {
      this.fileUploadComponent.msgs = [];
      this.fileUploadComponent.cd.markForCheck();
    }
  }

  @Input() disabled = false;

  @Output() filesChange = new EventEmitter<File[]>();

  public chooseButtonLabel = "";

  public get selectedFileCount(): number {
    return this.fileUploadComponent?.files?.length ?? this._tempFiles?.length ?? 0;
  }

  constructor(
    private readonly translocoService: TranslocoService,
    private readonly layoutService: LayoutService,
  ) {
    this.chooseButtonLabel = this.getUploadChooseLabel();
  }

  public ngAfterViewInit(): void {
    if (this._tempFiles) {
      this.fileUploadComponent.files = this._tempFiles;
      this._tempFiles = undefined;
    }
  }

  public async onSelect(event: FileSelectEvent): Promise<void> {
    const eventFiles: FileList = event.files as unknown as FileList;

    // This event fires and includes a payload even if the user selects an invalid file which the underlying
    // component ignores.
    // So first, we compare the list of what got added against the component's internal list of files.
    const validFiles: File[] = Array.from(eventFiles).filter((x) =>
      this.fileUploadComponent.files.some((f) => f === x),
    );

    // This will remove any zero-sized files from the upload component list
    await this.filterOutZeroSized(validFiles);

    // Only emit a change event if any of the selection actually made it into the list
    if (this.fileUploadComponent.files.length > 0) {
      this.filesChange.emit(this.fileUploadComponent.files);
    }
  }

  public onRemove(event: FileRemoveEvent): void {
    const updatedList = this.fileUploadComponent.files.filter((x) => x !== event.file);
    this.filesChange.emit(updatedList);
  }

  /**
   * Return PrimeNG-compatible CSS class strings for display icons for the given file.
   * @param file The File to get an icon for.
   * @returns A string suitable for displaying a PrimeNG pi-icon.
   */
  public getFileIcon(file: File): string {
    const fileType = file.type;
    const fileName = file.name.toLowerCase();

    // Basic mapping of common file types to PrimeIcons
    if (fileType.startsWith("application/pdf")) {
      return "pi pi-file-pdf";
    } else if (
      fileType.includes("spreadsheet") ||
      fileName.endsWith(".xlsx") ||
      fileName.endsWith(".csv") ||
      fileName.endsWith(".xls") ||
      fileName.endsWith(".ods")
    ) {
      return "pi pi-file-excel";
    } else if (
      fileType.includes("msword") ||
      fileType.includes("document") ||
      fileName.endsWith(".docx") ||
      fileName.endsWith(".doc") ||
      fileName.endsWith(".odt")
    ) {
      return "pi pi-file-word";
    } else if (fileType.startsWith("video/")) {
      return "pi pi-video";
    } else if (fileType.startsWith("audio/")) {
      return "pi pi-volume-down";
    } else if (
      fileType.includes("zip") ||
      fileType.includes("compressed") ||
      fileName.endsWith(".zip") ||
      fileName.endsWith(".rar") ||
      fileName.endsWith(".tar")
    ) {
      return "pi pi-folder";
    } else {
      return "pi pi-file";
    }
  }

  public removeFile(file: File): void {
    // Synthesize enough information to make the component remove the file from its internal list, and call
    // the onRemove() callback
    const index = this.fileUploadComponent.files.indexOf(file);
    if (index !== -1) {
      this.fileUploadComponent.remove(new Event("remove"), index);
    }
  }

  private getUploadChooseLabel(): string {
    return this.layoutService.isMobileUserAgent()
      ? this.translocoService.translate("app.fileUpload.browseFilesOrTakePhoto")
      : this.translocoService.translate("app.fileUpload.browseFiles");
  }

  // This handles an error case where the user has moved the file after selecting it,
  // or doesn't have permission to access it,
  // which results in us getting a file size of 0 and no MIME type.
  // (Attempting to read this file manually always results in a NotFoundError).
  private async filterOutZeroSized(selectedFiles: File[]): Promise<void> {
    for (const file of selectedFiles) {
      if (file.size > 0) {
        continue;
      }

      // Remove the file from the underlying FileUpload component without triggering handlers
      const index = this.fileUploadComponent.files.indexOf(file);
      if (index === -1) {
        // If the file has somehow vanished from inside the component, well, we don't need to do anything
        continue;
      }
      this.fileUploadComponent.files.splice(index, 1);

      // Attempt to force the browser to read the file's underlying data.
      let blob: ArrayBuffer | null = null;
      try {
        blob = await file.arrayBuffer();
      } catch (e) {
        let error: Error | null = null;
        if (e instanceof Error) {
          error = e;
        } else {
          error = new Error(`Unable to read file. Cause: ${e}`);
        }
        reportToSentry(error, "FileUpload.Component", {
          extra: {
            name: file.name,
            type: file.type,
            underlyingError: e,
          },
        });
      }

      // If it's still zero, add an error, we can't do anything else.
      if (!blob || blob.byteLength === 0) {
        reportToSentry(`FileUploadComponent was unable to fix file with size 0.`, "FileUpload.Component", {
          extra: {
            name: file.name,
            type: file.type,
          },
        });

        // HACK: The PrimeNG FileUpload component doesn't expose a nice API to display an error inside its box.
        // However, pushing a message object into its 'msgs' array will make it show up.
        // This is undocumented and *probably* liable to break between versions. Exercise caution.
        this.fileUploadComponent.msgs?.push({
          severity: "error",
          text: this.translocoService.translate("app.fileUpload.errorZeroSized", {
            name: file.name,
          }),
        } as UploadComponentError);
        // Force a re-render so the error shows up
        this.fileUploadComponent.cd.markForCheck();
        continue;
      }

      // However, if we were able to read the file's contents and manually get the length, swap out the object in
      // the array with a newly-created File object with the correct size and the bytes we just read.
      if (blob.byteLength !== 0 && file.size === 0) {
        reportToSentry(
          `FileUploadComponent was able to read 0-sized file's entire size, but needed to ` +
            `manually update the file.`,
          "FileUpload.Component",
          {
            extra: {
              name: file.name,
              type: file.type,
            },
          },
        );
        const recreatedFile = new File([blob], file.name, {
          lastModified: file.lastModified,
          type: file.type,
        });
        // Sneak it into the underlying component's list without going through validation
        // and force a re-render
        this.fileUploadComponent._files.push(recreatedFile);
        this.fileUploadComponent.cd.markForCheck();
      }
    }
  }
}

interface UploadComponentError {
  severity: PrimeNgMessageSeverity;
  text: string;
}
